/**
 * Mock progress note (QuestionnaireResponse + Questionnaire) for the Appointment page
 * when Medplum returns no note data. Renders the same as a real note via ProgressNoteCard.
 * Only returned for appointments that have a note in chart mock data (hasNote: true);
 * upcoming/scheduled appointments without a note get no mock so the page shows add-note UI.
 */
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { mockAppointments } from '../clients/data';
import { PROGRESS_NOTE_QUESTIONNAIRE } from './noteTemplates';
import { appointmentTag, PROVIDER_SIGNATURE_EXT_URL } from './fhirNoteMapping';
import { MOCK_UNSIGNED_NOTE_APPOINTMENT_ID, MOCK_ADDENDUM_APPOINTMENT_ID } from './addendums/constants';
import { extractQRTextLines, computeAddendumLineChanges } from './addendums/noteDiffUtils';
import type { Addendum } from './addendums/types';
import type { AppointmentResponseEntry } from './hooks';
import { getClientChartData, mockRelationships } from '../../data/clientChartApi';
import { mockClients } from '../caseload/data';

const MOCK_QR_ID = 'mock-progress-note-qr';

/** True if this QuestionnaireResponse id is from mock data (save to local state only, not Medplum). */
export function isMockQuestionnaireResponseId(id: string | undefined): boolean {
  return !!id?.startsWith(`${MOCK_QR_ID}-`);
}

/** Build a single response item with valueString answer. */
function item(linkId: string, text: string, valueString: string): QuestionnaireResponseItem {
  return { linkId, text, answer: [{ valueString }] };
}

/** Build a choice response item. */
function choiceItem(linkId: string, text: string, valueString: string): QuestionnaireResponseItem {
  return { linkId, text, answer: [{ valueString }] };
}

function cloneQuestionnaireResponse(qr: QuestionnaireResponse): QuestionnaireResponse {
  return JSON.parse(JSON.stringify(qr)) as QuestionnaireResponse;
}

/** Set valueString on a leaf item anywhere in the tree (by linkId). */
function setAnswerByLinkId(
  items: QuestionnaireResponseItem[] | undefined,
  linkId: string,
  value: string,
): void {
  if (!items) return;
  for (const it of items) {
    if (it.linkId === linkId && it.answer?.[0] && 'valueString' in it.answer[0]) {
      (it.answer[0] as { valueString?: string }).valueString = value;
      return;
    }
    if (it.item?.length) setAnswerByLinkId(it.item, linkId, value);
  }
}

/** Mock progress note content matching the Meridian progress note template structure.
 * Demo data is realistic and useful for showing the intended provider workflow. */
export function buildMockProgressNoteResponse(
  appointmentId: string,
  options?: { signed?: boolean },
): QuestionnaireResponse {
  const signed = options?.signed !== false;
  const tag = appointmentTag(appointmentId);
  const base = {
    resourceType: 'QuestionnaireResponse',
    id: `${MOCK_QR_ID}-${appointmentId}`,
    status: signed ? 'completed' : ('in-progress' as const),
    questionnaire: `Questionnaire/${PROGRESS_NOTE_QUESTIONNAIRE.id}`,
    meta: { tag: [tag] },
    item: [
      item(
        'presenting-focus',
        'Presenting focus / Chief concern for this session',
        'Follow-up on sleep and mood; continued work stress and anxiety after receiving critical feedback at work. Client wanted to focus on managing catastrophic thoughts and behavioral activation.',
      ),
      {
        linkId: 'mse-observations',
        text: 'MSE Observations:',
        item: [
          choiceItem('mse-affect', 'Affect', 'Full Range'),
          choiceItem('mse-cognitive', 'Cognitive Functioning', 'Alert and Oriented'),
          choiceItem('mse-functional', 'Functional Status', 'Independent'),
          choiceItem('mse-interpersonal', 'Interpersonal', 'Engaged'),
          choiceItem('mse-mood', 'Mood', 'Euthymic'),
        ],
      },
      item(
        'session-content',
        'Content or topics discussed specific to this session (please describe):',
        'Client presented for weekly individual therapy. Reported improved sleep over the past week (5–6 hours per night) after implementing sleep hygiene and a consistent bedtime. Discussed ongoing work-related stress; client identified a pattern of catastrophic thinking when receiving feedback. Used Socratic questioning and cognitive restructuring to challenge “I’m going to be fired” belief. Explored behavioral activation: client agreed to schedule two pleasurable activities before next session. Denies current SI/HI; no safety concerns. Client was receptive to homework and asked for a thought record handout.',
      ),
      {
        linkId: 'interventions-utilized',
        text: 'Interventions Utilized:',
        answer: [
          { valueString: 'CBT Interventions and Skills' },
          { valueString: 'Psychoeducation' },
          { valueString: 'Reflective Listening' },
          { valueString: 'Behavioral Activation' },
        ],
      },
      item(
        'client-response-interventions',
        "Client's response to interventions this session (please describe):",
        'Client engaged actively in cognitive restructuring and was able to generate an alternative thought (“I can use this feedback to improve”). Affect brightened when discussing small wins with sleep. Agreed to behavioral activation and thought records. Good insight into link between stress and sleep. No resistance; collaborative throughout.',
      ),
      item(
        'client-progress-goals',
        "Client's progress toward treatment plan goals (please describe):",
        'Goal 1 (reduce depression symptoms): PHQ-9 down from 14 to 10; client reports better mood and energy. Goal 2 (improve sleep): Implementing routine; sleep log shows improvement. Goal 3 (manage work stress): Beginning to use cognitive strategies; will continue in next session. Overall good progress; treatment plan and frequency remain appropriate.',
      ),
      item(
        'clinical-impression',
        'Clinical impression / Assessment',
        'Client presents with moderate depressive symptoms (improving) and anxiety tied to work performance. Good engagement and insight; no acute risk. Formulation: Core beliefs about inadequacy activated by work feedback; behavioral avoidance contributing to low mood. Current interventions (CBT, behavioral activation) are appropriate. Risk level: Low. Continue current treatment plan.',
      ),
      choiceItem(
        'safety-screening',
        'Client denies suicidal and homicidal ideation today?',
        'Yes, denies SI/HI',
      ),
      {
        linkId: 'risk-factors',
        text: 'Risk Factors:',
        answer: [{ valueString: 'None' }],
      },
      choiceItem(
        'therapist-consulted-risk',
        'The therapist consulted with another provider or supervisor regarding identified risk factors:',
        'N/A',
      ),
      choiceItem('medication-changes', 'Medication Changes:', 'No changes'),
      item(
        'recommendations-homework',
        'Recommendations, plan, or homework assigned before the next session:',
        'Continue weekly therapy. Homework: (1) Complete thought records when stress or automatic thoughts occur; (2) Schedule and complete two pleasurable activities; (3) Continue sleep log. Administer PHQ-9 at next visit. Next session in 7 days; will review homework and continue cognitive strategies for work stress.',
      ),
    ],
    extension: signed
      ? [
          {
            url: PROVIDER_SIGNATURE_EXT_URL,
            valueString: JSON.stringify({
              name: 'Dr. Emily Chen',
              license: 'PhD, LP',
              signedAt: new Date().toISOString(),
            }),
          },
        ]
      : undefined,
  };
  return base as QuestionnaireResponse;
}

/**
 * Build a QuestionnaireResponse from a ClinicalNote's SOAP content.
 * Used for per-client notes (e.g. family/couples therapy) that have
 * structured SOAP data stored in mockPerClientData sharedNotes.
 */
function buildNoteResponseFromSoap(
  appointmentId: string,
  soap: { subjective: string; objective: string; assessment: string; plan: string },
  opts: {
    signed: boolean;
    signedBy?: string;
    signedDate?: string;
    interventions?: string[];
  },
): QuestionnaireResponse {
  const tag = appointmentTag(appointmentId);
  const { signed, signedBy, signedDate, interventions } = opts;

  return {
    resourceType: 'QuestionnaireResponse',
    id: `${MOCK_QR_ID}-${appointmentId}`,
    status: signed ? 'completed' : ('in-progress' as const),
    questionnaire: `Questionnaire/${PROGRESS_NOTE_QUESTIONNAIRE.id}`,
    meta: { tag: [tag] },
    item: [
      item('presenting-focus', 'Presenting focus / Chief concern for this session', soap.subjective),
      {
        linkId: 'mse-observations',
        text: 'MSE Observations:',
        item: [
          choiceItem('mse-affect', 'Affect', 'Appropriate'),
          choiceItem('mse-cognitive', 'Cognitive Functioning', 'Alert and Oriented'),
          choiceItem('mse-functional', 'Functional Status', 'Independent'),
          choiceItem('mse-interpersonal', 'Interpersonal', 'Engaged'),
          choiceItem('mse-mood', 'Mood', 'Euthymic'),
        ],
      },
      item('session-content', 'Content or topics discussed specific to this session (please describe):', soap.objective),
      {
        linkId: 'interventions-utilized',
        text: 'Interventions Utilized:',
        answer: (interventions ?? ['Psychoeducation', 'Reflective Listening', 'Exploration of Relationships', 'DBT Interventions and Skills']).map(
          (v) => ({ valueString: v }),
        ),
      },
      item("client-response-interventions", "Client's response to interventions this session (please describe):",
        'All family members engaged and cooperative throughout session. Progress consistent with treatment plan goals.'),
      item("client-progress-goals", "Client's progress toward treatment plan goals (please describe):", soap.assessment),
      item('clinical-impression', 'Clinical impression / Assessment', soap.assessment),
      choiceItem('safety-screening', 'Client denies suicidal and homicidal ideation today?', 'Yes, denies SI/HI'),
      { linkId: 'risk-factors', text: 'Risk Factors:', answer: [{ valueString: 'None' }] },
      choiceItem('therapist-consulted-risk', 'The therapist consulted with another provider or supervisor regarding identified risk factors:', 'N/A'),
      choiceItem('medication-changes', 'Medication Changes:', 'N/A'),
      item('recommendations-homework', 'Recommendations, plan, or homework assigned before the next session:', soap.plan),
    ],
    extension: signed
      ? [
          {
            url: PROVIDER_SIGNATURE_EXT_URL,
            valueString: JSON.stringify({
              name: signedBy ?? 'Dr. Smith',
              license: 'LMFT',
              signedAt: signedDate ? new Date(signedDate).toISOString() : new Date().toISOString(),
            }),
          },
        ]
      : undefined,
  } as QuestionnaireResponse;
}

/**
 * Returns mock note entry (QuestionnaireResponse + Questionnaire) for the appointment page
 * when there is no Medplum note data and the appointment has a note in chart mock data (hasNote: true).
 * Returns null for upcoming/scheduled appointments without a note so the page shows add-note UI.
 *
 * Search order:
 *  1. Shared relationship appointments (mockRelationships) — checked first because they contain
 *     the SOAP content directly and don't require a per-client chart data lookup.
 *  2. Legacy global data (Sarah Johnson's mockAppointments).
 *  3. Per-client chart data for all other clients.
 *
 * For APT-002 the mock note is returned unsigned (no signature) to demo addendum locked state.
 */
export function getMockNoteEntryForAppointment(
  appointmentId: string,
): AppointmentResponseEntry | null {
  // 1. Shared relationship appointments — the authoritative source for couples/family appointments.
  for (const rel of mockRelationships) {
    const sharedApt = rel.sharedAppointments.find((a) => a.id === appointmentId);
    if (!sharedApt) continue;
    if (!sharedApt.hasNote) return null;

    const linkedNote = rel.sharedNotes.find((n) => n.appointmentId === appointmentId);
    if (linkedNote?.soapNote) {
      return {
        response: buildNoteResponseFromSoap(appointmentId, linkedNote.soapNote, {
          signed: linkedNote.status === 'Signed',
          signedBy: (linkedNote as { signedBy?: string }).signedBy,
          signedDate: (linkedNote as { signedDate?: string }).signedDate,
        }),
        questionnaire: PROGRESS_NOTE_QUESTIONNAIRE,
      };
    }

    // hasNote: true but no SOAP content in the shared note
    return null;
  }

  // 2. Legacy: Sarah Johnson's chart appointments
  const legacyAppointment = mockAppointments.find((a) => a.id === appointmentId);
  if (legacyAppointment) {
    if (!legacyAppointment.hasNote) return null;
    const signed = appointmentId !== MOCK_UNSIGNED_NOTE_APPOINTMENT_ID;
    return {
      response: buildMockProgressNoteResponse(appointmentId, { signed }),
      questionnaire: PROGRESS_NOTE_QUESTIONNAIRE,
    };
  }

  // 3. Per-client chart data (non-shared appointments)
  for (const client of mockClients) {
    const chartData = getClientChartData(client.id);
    const apt = chartData.appointments.find((a) => a.id === appointmentId);
    if (!apt) continue;
    if (!apt.hasNote) return null;

    const linkedNote = chartData.clinicalNotes.find((n) => n.appointmentId === appointmentId);
    if (linkedNote?.soapNote) {
      return {
        response: buildNoteResponseFromSoap(appointmentId, linkedNote.soapNote, {
          signed: linkedNote.status === 'Signed',
          signedBy: linkedNote.signedBy,
          signedDate: linkedNote.signedDate,
        }),
        questionnaire: PROGRESS_NOTE_QUESTIONNAIRE,
      };
    }

    // hasNote: true but no linked SOAP note — use generic template
    const signed = appointmentId !== MOCK_UNSIGNED_NOTE_APPOINTMENT_ID;
    return {
      response: buildMockProgressNoteResponse(appointmentId, { signed }),
      questionnaire: PROGRESS_NOTE_QUESTIONNAIRE,
    };
  }

  return null;
}

const MOCK_ORIGINAL_NOTE_DATE = '2026-01-21T08:00:00Z';

const PLAN_APPEND =
  ' Consider adding mindfulness practice between sessions.';

function patchRecommendationsLine(lines: string[], append: string): string[] {
  const planPrefix = 'Recommendations, plan, or homework assigned before the next session:';
  return lines.map((line) => {
    if (line.startsWith(planPrefix)) {
      const colon = line.indexOf(': ');
      if (colon >= 0) {
        return `${line.slice(0, colon + 2)}${line.slice(colon + 2).trimEnd()}${append}`;
      }
    }
    return line;
  });
}

/**
 * Mock addendums for APT-001: line snapshots are derived from the same QR + questionnaire
 * as the progress note above via extractQRTextLines, so expansion content always matches the note.
 */
export function getMockAddendumsForAppointment(
  appointmentId: string | undefined,
  _originalNoteDate: string,
): Addendum[] {
  if (!appointmentId || appointmentId !== MOCK_ADDENDUM_APPOINTMENT_ID) {
    return [];
  }

  const q = PROGRESS_NOTE_QUESTIONNAIRE;
  const qrCurrent = buildMockProgressNoteResponse(appointmentId, { signed: true });
  const linesAfterAddendum1 = extractQRTextLines(qrCurrent, q);

  const qrBeforeAdd1 = cloneQuestionnaireResponse(qrCurrent);
  setAnswerByLinkId(qrBeforeAdd1.item, 'mse-affect', 'Constricted');
  setAnswerByLinkId(qrBeforeAdd1.item, 'mse-mood', 'Anxious');
  const linesBeforeAddendum1 = extractQRTextLines(qrBeforeAdd1, q);

  const lineChanges1 = computeAddendumLineChanges(linesBeforeAddendum1, linesAfterAddendum1);

  const linesAfterAddendum2 = patchRecommendationsLine(linesAfterAddendum1, PLAN_APPEND);

  const lineChanges2 = computeAddendumLineChanges(linesAfterAddendum1, linesAfterAddendum2);

  return [
    {
      id: 'add-1',
      label: 'Revised Note #1',
      authorName: 'Dr. Emily Chen',
      authorCredentials: 'PhD, LP',
      timestamp: '2026-01-21T14:30:00Z',
      originalNoteDate: MOCK_ORIGINAL_NOTE_DATE,
      body: '',
      reasonForChange: 'clarification' as const,
      lineChanges: lineChanges1,
      originalLines: linesBeforeAddendum1,
      afterLines: linesAfterAddendum1,
    },
    {
      id: 'add-2',
      label: 'Revised Note #2',
      authorName: 'Dr. Emily Chen',
      authorCredentials: 'PhD, LP',
      timestamp: '2026-01-22T09:15:00Z',
      originalNoteDate: MOCK_ORIGINAL_NOTE_DATE,
      body: 'Updated after client follow-up call.',
      reasonForChange: 'clarification' as const,
      lineChanges: lineChanges2,
      originalLines: linesAfterAddendum1,
      afterLines: linesAfterAddendum2,
    },
  ];
}
